import {
  Box,
  Text,
  Divider,
  Flex,
  Button,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { useContext, useEffect, useRef, useState } from 'react';
import { MeContext } from '../../../context/MeContext';
import { HttpContext } from '../../../context/HttpContext';
import { AuthContext } from '../../../context/AuthContext';
import SendInvitationModal from '../../modals/SendInvitationModal';
import ResendConfirmationModal from './ResendConfirmationModal';
import sentry from '../../../sentry';
import CustomToast from '../../../common/CustomToast';
import TeamInvitationTable from './TeamInvitationTable';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import LockedActionButton from '../../common/LockedActionButton';

function TeamInvitations() {
  const modeBoxBg = useColorModeValue('gradient', 'secondaryDark');
  const modeTextBg = useColorModeValue('quaternaryBackground', 'primaryDark06');
  const modeBorderColor = useColorModeValue('primaryBackground', 'primaryDark');
  const meCtx = useContext(MeContext);
  const { fetchData } = meCtx;
  const isOrgAdmin = meCtx.isOrgAdmin();
  const isReadOnly = useIsReadOnly();
  const { invitations, invitationLimit, entities } = meCtx.state;
  const [isSendInvitationOpen, setIsSendInvitationOpen] = useState(false);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const { authAxios } = useContext(HttpContext);
  const toast = useToast();
  const authContext = useContext(AuthContext);

  const onSendInvitationOpen = () => {
    setIsSendInvitationOpen(true);
  };

  const onSendInvitationClose = () => {
    setIsSendInvitationOpen(false);
  };

  const openResendModal = (email, role, entityId, companyName) => {
    setSelectedInvitation({ email, role, entityId, companyName });
    setIsResendModalOpen(true);
  };

  const closeResendModal = () => {
    setIsResendModalOpen(false);
    setSelectedInvitation(null);
  };

  const confirmResend = async () => {
    if (!selectedInvitation) return;

    const { email, role, entityId, companyName } = selectedInvitation;
    setIsResending(true);

    try {
      await authAxios.post(
        `accounts/team_member_invitation`,
        {
          invitation: {
            email: email,
            role: role,
            entity_id: role === 'ent_admin' ? entityId : null,
            company_name: companyName,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${authContext.getAccessToken()}`,
          },
        }
      );
      toast({
        render: (props) => (
          <CustomToast
            status="success"
            title="Invitation sent."
            description="You have successfully resent the invitation."
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });
    } catch (onError) {
      sentry.sendError(onError);
      console.log(onError);
      const errors = onError.response?.data?.errors || [
        'An error occurred during resend',
      ];
      errors.forEach((error) => {
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title={error}
              description=""
              onClose={props.onClose}
            />
          ),
        });
      });
    } finally {
      setIsResending(false);
      closeResendModal();
    }
  };

  const handleResendInvitation = openResendModal;

  const getEntityName = (entityId) => {
    const entity = entities.find((entity) => entity.id === entityId);
    return entity ? entity.name : 'All entities';
  };

  useEffect(() => {
    fetchData({ inviteSent: false });
  }, [fetchData]);

  const EmptyStateMessage = ({ isOrgAdmin }) => (
    <Box p="14px 24px">
      <Text textStyle="bodyRoman" color="secondaryDark08">
        {isOrgAdmin
          ? 'You have not sent any invitations.'
          : "You don't have any team members yet."}
      </Text>
    </Box>
  );

  return (
    <>
      <Box
        className="autofillForDarkBg"
        w="full"
        borderRadius="15px"
        border="0.5px solid #D9D9D9"
        boxShadow="0px 5px 10px rgba(0, 0, 0, 0.05)"
        overflow="hidden"
      >
        <Box bg={modeBoxBg} borderRadius="15px" pb={0}>
          <Flex
            direction="row"
            justifyContent="space-between"
            align="center"
            p="14px 14px 14px 24px"
            bg={modeTextBg}
            borderRadius="15px 15px 0 0"
          >
            <Flex direction="column">
              <Text fontSize="18px" textStyle="headingFamilyMedium">
                Team
              </Text>
              {isOrgAdmin && (
                <Text
                  fontSize="14px"
                  mt={0.5}
                  textStyle="bodyRoman"
                  color="secondaryDark08"
                  bg={modeTextBg}
                >
                  {invitationLimit} invitation{invitationLimit === 1 ? '' : 's'}{' '}
                  left
                </Text>
              )}
            </Flex>
            {isOrgAdmin &&
              (isReadOnly ? (
                <LockedActionButton gap={2} size="xs">
                  Send invite
                </LockedActionButton>
              ) : (
                <Button
                  onClick={() => onSendInvitationOpen()}
                  gap={2}
                  size="xs"
                  isDisabled={invitationLimit === 0}
                >
                  Send invite
                </Button>
              ))}
          </Flex>
          <Divider borderColor={modeBorderColor} />
          {invitations.length === 0 && (
            <EmptyStateMessage isOrgAdmin={isOrgAdmin} />
          )}
          {invitations.length > 0 && (
            <TeamInvitationTable
              invitations={invitations}
              handleResendInvitation={handleResendInvitation}
              getEntityName={getEntityName}
            />
          )}
        </Box>
      </Box>
      <SendInvitationModal
        isOpen={isSendInvitationOpen}
        onClose={onSendInvitationClose}
      />
      <ResendConfirmationModal
        isOpen={isResendModalOpen}
        onClose={closeResendModal}
        onConfirm={confirmResend}
        email={selectedInvitation?.email || ''}
        isLoading={isResending}
      />
    </>
  );
}

export default TeamInvitations;
